import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeaderLoggedUser from "./partials/HeaderLoggedUser";
import {getCookie} from "./utils/cookies";
import '../css/EditProfile.css';
import GoogleBusinessFields from './partials/GoogleBusinessFields';
import { getGoogleBusinessInputError } from './utils/googleBusinessInput';

const API_AUTH_URL = process.env.REACT_APP_AUTH_API_URL;

export default function EditProfile() {
    const [formData, setFormData] = useState({
        description: '',
        vehicles: [''],
        googleBusinessUrl: '',
        googlePlaceId: '',
    });

    const [feedback, setFeedback] = useState(null);
    const [photoError, setPhotoError] = useState('');
    const [loadError, setLoadError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [photos, setPhotos] = useState([]);
    const [profilePhotoId, setProfilePhotoId] = useState(null);
    const [loading, setLoading] = useState(true);

    const token = getCookie('MMAUTH');

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_AUTH_URL}/auth/getLoggedFuneralHonorData`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'MmTkn': token
                    },
                    cache: 'no-store',
                });

                if (!response.ok) {
                    throw new Error('Errore nel caricamento');
                }

                const data = await response.json();

                // Aggiorna i campi del form
                setFormData({
                    description: data.description || '',
                    vehicles: data.vehicles && data.vehicles.length > 0 ? data.vehicles : [''],
                    googleBusinessUrl: data.googleBusinessUrl || '',
                    googlePlaceId: data.googlePlaceId || '',
                });

                // Carica le immagini esistenti
                if (data.images && data.images.length > 0) {
                    const loadedPhotos = data.images.map((url, index) => ({
                        id: `existing-${index}-${Date.now()}`,
                        url: url,
                        isExisting: true
                    }));
                    setPhotos(loadedPhotos);
                    // La prima immagine è il logo (display_order = 0)
                    setProfilePhotoId(loadedPhotos[0].id);
                }
            } catch (error) {
                setLoadError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [token]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files);
        e.target.value = '';
        setPhotoError('');
        const maxPhotos = 5; // 1 logo + 4 foto

        if (photos.length + files.length > maxPhotos) {
            setPhotoError(`Puoi caricare al massimo ${maxPhotos} immagini. Rimuovine una per aggiungerne un'altra.`);
            return;
        }

        files.forEach(file => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const newPhoto = {
                        id: `new-${Date.now()}-${Math.random()}`,
                        url: e.target.result,
                        file: file,
                        isExisting: false
                    };
                    setPhotos(prev => {
                        const updated = [...prev, newPhoto];
                        // Se è la prima foto, impostala come profilo
                        if (updated.length === 1) {
                            setProfilePhotoId(newPhoto.id);
                        }
                        return updated;
                    });
                };
                reader.readAsDataURL(file);
            }
        });
    };

    const removePhoto = (photoId) => {
        setPhotoError('');
        setPhotos(prev => {
            const filtered = prev.filter(photo => photo.id !== photoId);
            // Se rimuovo la foto profilo, imposta la prima rimanente come profilo
            if (photoId === profilePhotoId && filtered.length > 0) {
                setProfilePhotoId(filtered[0].id);
            } else if (filtered.length === 0) {
                setProfilePhotoId(null);
            }
            return filtered;
        });
    };

    const setAsProfilePhoto = (photoId) => {
        setProfilePhotoId(photoId);
    };

    const handleVehicleChange = (index, value) => {
        const newVehicles = [...formData.vehicles];
        newVehicles[index] = value;
        setFormData(prev => ({ ...prev, vehicles: newVehicles }));
    };

    const addVehicle = () => {
        setFormData(prev => ({ ...prev, vehicles: [...prev.vehicles, ''] }));
    };

    const removeVehicle = (index) => {
        if (formData.vehicles.length > 1) {
            const newVehicles = formData.vehicles.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, vehicles: newVehicles }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSaving) return;
        const googleBusinessError = getGoogleBusinessInputError(formData);
        if (googleBusinessError) { setFeedback({ type: 'error', message: googleBusinessError }); return; }
        setIsSaving(true);
        setFeedback(null);

        try {
            const submitData = new FormData();

            // Descrizione
            submitData.append('description', formData.description);
            submitData.append('googleBusinessUrl', formData.googleBusinessUrl.trim());
            submitData.append('googlePlaceId', formData.googlePlaceId.trim());

            // Vehicles
            const filteredVehicles = formData.vehicles.filter(v => v.trim() !== '');
            filteredVehicles.forEach(vehicle => {
                submitData.append('vehicles[]', vehicle);
            });

            // PhotoOrder
            photos.forEach((photo, index) => {
                submitData.append(`photoOrder[${index}][url]`, photo.url);
                submitData.append(`photoOrder[${index}][isLogo]`, photo.id === profilePhotoId ? 1 : 0);
                submitData.append(`photoOrder[${index}][isExisting]`, photo.isExisting ? 1 : 0);
            });

            // Solo nuove foto come file
            photos
                .filter(photo => !photo.isExisting && photo.file)
                .forEach(photo => {
                    submitData.append('newPhotos[]', photo.file);
                });

            const response = await fetch(`${API_AUTH_URL}/auth/updateFuneralHonorData`, {
                method: 'POST',
                headers: {
                    "MmTkn": token
                },
                body: submitData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || errorData.message || "Errore nell'aggiornamento");
            }

            const result = await response.json();
            setFeedback({ type: 'success', message: result.message || 'Profilo aggiornato con successo.' });

        } catch (error) {
            setFeedback({ type: 'error', message: error.message || 'Errore nel salvataggio delle modifiche. Riprova.' });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="ef-page ef-edit-profile">
            <HeaderLoggedUser />
            <main className="ef-edit-profile__main">
                <header className="ef-edit-profile__heading">
                    <Link to="/profilo" className="ef-edit-profile__back"><span aria-hidden="true">←</span> Il mio profilo</Link>
                    <p className="ef-edit-profile__eyebrow">Area riservata</p>
                    <h1>Il profilo della tua agenzia</h1>
                    <p>Aggiorna le immagini, racconta la tua attività e collega le recensioni Google della tua agenzia.</p>
                </header>

                {loading ? (
                    <div className="ef-edit-profile__state" role="status">
                        <span className="ef-edit-profile__spinner" aria-hidden="true" />
                        Caricamento del profilo…
                    </div>
                ) : loadError ? (
                    <div className="ef-edit-profile__state">
                        <p role="alert">Non è stato possibile caricare il profilo. Riprova tra qualche istante.</p>
                        <button type="button" className="ef-edit-profile__save" onClick={() => window.location.reload()}>Riprova</button>
                    </div>
                ) : (
                    <form className="ef-edit-profile__form" onSubmit={handleSubmit} onChange={() => setFeedback(null)} aria-busy={isSaving}>
                        <fieldset disabled={isSaving} className="ef-edit-profile__fields">
                            <legend className="sr-only">Modifica il profilo della tua onoranza funebre</legend>

                            <section className="ef-edit-profile__section" aria-labelledby="profile-images-title">
                                <div className="ef-edit-profile__section-heading">
                                    <span className="ef-edit-profile__section-number" aria-hidden="true">01</span>
                                    <h2 id="profile-images-title">Immagini e logo</h2>
                                    <p>Presenta la tua agenzia con un logo e le foto dei tuoi spazi. Puoi aggiungere fino a 5 immagini.</p>
                                </div>
                                <div className="ef-edit-profile__section-content">
                                    {photos.length > 0 && (
                                        <div className="ef-edit-profile__photos">
                                            {photos.map((photo, index) => (
                                                <figure className={`ef-edit-profile__photo${photo.id === profilePhotoId ? ' ef-edit-profile__photo--logo' : ''}`} key={photo.id}>
                                                    <div className="ef-edit-profile__photo-preview">
                                                        <img src={photo.url} alt={photo.id === profilePhotoId ? "Logo dell'agenzia" : `Foto dell'agenzia ${index + 1}`} />
                                                        {photo.id === profilePhotoId && <span className="ef-edit-profile__logo-badge">Logo</span>}
                                                    </div>
                                                    <figcaption>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setAsProfilePhoto(photo.id); setFeedback(null); }}
                                                            aria-label={`Usa la foto ${index + 1} come logo`}
                                                            aria-pressed={photo.id === profilePhotoId}
                                                            className="ef-edit-profile__photo-logo"
                                                            title="Imposta come logo"
                                                        >
                                                            <span aria-hidden="true">☆</span> Logo
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => { removePhoto(photo.id); setFeedback(null); }}
                                                            aria-label={`Rimuovi la foto ${index + 1}`}
                                                            title="Rimuovi foto"
                                                            className="ef-edit-profile__remove"
                                                        ><span aria-hidden="true">×</span></button>
                                                    </figcaption>
                                                </figure>
                                            ))}
                                        </div>
                                    )}
                                    <div className={`ef-edit-profile__upload${photos.length >= 5 ? ' ef-edit-profile__upload--disabled' : ''}`}>
                                        <input
                                            type="file"
                                            id="photo-upload"
                                            accept="image/*"
                                            multiple
                                            onChange={handlePhotoUpload}
                                            className="sr-only"
                                            disabled={photos.length >= 5}
                                            aria-describedby="profile-images-help"
                                        />
                                        <label htmlFor="photo-upload">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M12 16V4m-4 4 4-4 4 4M4 15v5h16v-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            <span>{photos.length >= 5 ? 'Hai aggiunto tutte le immagini' : 'Aggiungi immagini'}<small>{photos.length} di 5 immagini</small></span>
                                        </label>
                                    </div>
                                    <p id="profile-images-help" className="ef-edit-profile__hint">La stella indica l'immagine da utilizzare come logo.</p>
                                    {photoError && <p className="ef-edit-profile__photo-error" role="alert">{photoError}</p>}
                                </div>
                            </section>

                            <section className="ef-edit-profile__section" aria-labelledby="profile-description-title">
                                <div className="ef-edit-profile__section-heading">
                                    <span className="ef-edit-profile__section-number" aria-hidden="true">02</span>
                                    <h2 id="profile-description-title">La tua attività</h2>
                                    <p>Aiuta le famiglie a conoscerti: racconta la tua esperienza e l'attenzione che dedichi a ogni servizio.</p>
                                </div>
                                <div className="ef-edit-profile__section-content">
                                    <label htmlFor="profile-description" className="ef-edit-profile__label">Descrizione dell'agenzia</label>
                                    <textarea
                                        id="profile-description"
                                        name="description"
                                        placeholder="Racconta la storia della tua agenzia, i servizi offerti e ciò che vi distingue…"
                                        value={formData.description}
                                        rows={6}
                                        onChange={handleInputChange}
                                        aria-describedby="profile-description-help"
                                    />
                                    <p id="profile-description-help" className="ef-edit-profile__hint">Questa descrizione sarà visibile nella scheda della tua agenzia.</p>
                                </div>
                            </section>

                            <section className="ef-edit-profile__section" aria-labelledby="profile-vehicles-title">
                                <div className="ef-edit-profile__section-heading">
                                    <span className="ef-edit-profile__section-number" aria-hidden="true">03</span>
                                    <h2 id="profile-vehicles-title">Parco auto</h2>
                                    <p>Indica i mezzi disponibili per il servizio. Puoi specificare marca, modello e colore.</p>
                                </div>
                                <div className="ef-edit-profile__section-content">
                                    <div className="ef-edit-profile__vehicles">
                                        {formData.vehicles.map((vehicle, index) => (
                                            <div key={index}>
                                                <label htmlFor={`profile-vehicle-${index}`} className="ef-edit-profile__label">Mezzo {index + 1}</label>
                                                <div className="ef-edit-profile__vehicle">
                                                    <input
                                                        id={`profile-vehicle-${index}`}
                                                        type="text"
                                                        placeholder="Es. Mercedes Classe E, nero"
                                                        value={vehicle}
                                                        onChange={(event) => handleVehicleChange(index, event.target.value)}
                                                    />
                                                    {formData.vehicles.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => { removeVehicle(index); setFeedback(null); }}
                                                            className="ef-edit-profile__remove"
                                                            aria-label={`Rimuovi il mezzo ${index + 1}`}
                                                            title="Rimuovi mezzo"
                                                        ><span aria-hidden="true">×</span></button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <button type="button" onClick={() => { addVehicle(); setFeedback(null); }} className="ef-edit-profile__add">
                                        <span aria-hidden="true">＋</span> Aggiungi un mezzo
                                    </button>
                                </div>
                            </section>
                            <section className="ef-edit-profile__section" aria-labelledby="profile-google-title">
                                <div className="ef-edit-profile__section-heading">
                                    <span className="ef-edit-profile__section-number" aria-hidden="true">04</span>
                                    <h2 id="profile-google-title">Recensioni Google</h2>
                                    <p>Mostra alle famiglie la valutazione della tua attività. Puoi sostituire o rimuovere la scheda collegata.</p>
                                </div>
                                <div className="ef-edit-profile__section-content">
                                    <GoogleBusinessFields googleBusinessUrl={formData.googleBusinessUrl} googlePlaceId={formData.googlePlaceId}
                                        disabled={isSaving} onChange={value => { setFormData(prev => ({ ...prev, ...value })); setFeedback(null); }} />
                                </div>
                            </section>
                        </fieldset>

                        <div className="ef-edit-profile__actions">
                            <div className="ef-edit-profile__save-note">
                                <strong>{isSaving ? 'Aggiornamento del profilo' : 'Tutto pronto?'}</strong>
                                <p>{isSaving ? 'Attendi il completamento del salvataggio.' : 'Salva le modifiche per aggiornare la tua scheda.'}</p>
                            </div>
                            <button type="submit" disabled={isSaving} className="ef-edit-profile__save">
                                {isSaving ? <span className="ef-edit-profile__spinner" aria-hidden="true" /> : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m5 12 4 4L19 6" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                                {isSaving ? 'Salvataggio in corso…' : 'Salva modifiche'}
                            </button>
                        </div>
                        {feedback && (
                            <div className={`ef-edit-profile__feedback ef-edit-profile__feedback--${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}>
                                {feedback.message}
                            </div>
                        )}
                    </form>
                )}

                <aside className="ef-edit-profile__help">
                    <span className="ef-edit-profile__help-icon" aria-hidden="true">i</span>
                    <p>Vuoi modificare altri dati della tua agenzia? <Link to="/contatti">Contatta l'assistenza <span aria-hidden="true">↗</span></Link></p>
                </aside>
            </main>
        </div>
    );
}
